import type { UIMessage } from 'ai'

// Type guard to check if it's a tool part
export function isToolPart(
    part: UIMessage['parts'][number] | null | undefined,
): part is Extract<UIMessage['parts'][number], { type: `tool-${string}` }> {
    if (!part) return false
    return part.type.startsWith('tool-')
}

// Returns true if the part is a reasoning part, false otherwise
export function isReasoningPart(
    part: UIMessage['parts'][number] | null | undefined,
): part is Extract<UIMessage['parts'][number], { type: 'reasoning' }> {
    if (!part) return false
    return part.type === 'reasoning'
}

// Type guard to check if it's a text part
export function isTextPart(
    part: UIMessage['parts'][number] | null | undefined,
): part is Extract<UIMessage['parts'][number], { type: 'text' }> {
    if (!part) return false
    return part.type === 'text'
}

export function hasTextPart(message?: UIMessage): boolean {
    if (!message) return false
    return message.parts.some((part) => isTextPart(part))
}

export function isStreamingPart(
    part: UIMessage['parts'][number] | null | undefined,
): part is Extract<UIMessage['parts'][number], { streaming?: boolean }> {
    if (!part) return false
    if ('state' in part) {
        return part.state === 'streaming' || part.state === 'input-streaming'
    }
    return false
}

export function getPartState(
    part: UIMessage['parts'][number] | null | undefined,
): string | undefined {
    if (!part) return undefined
    if ('state' in part) {
        return part.state
    }
    return undefined
}

export function isLoadingState(state?: string): boolean {
    if (!state) return false
    return ['streaming', 'input-available'].includes(state)
}

// Utility function to get the text content of a message, if it exists
export function getTextPart(message: UIMessage): string | undefined {
    const textPart = message.parts.find((part) => isTextPart(part))
    return textPart?.text
}

// Get contrast color (black or white) based on background color for readability
export function getContrast(hexcolor: string = '000000'): 'black' | 'white' {
    let hex = hexcolor.replace('#', '')

    // Expand 3-character hex to 6-character (e.g., #ccc -> #cccccc)
    if (hex.length === 3) {
        hex = hex
            .split('')
            .map((char) => char + char)
            .join('')
    }

    return parseInt(hex, 16) > 0xffffff / 2 ? 'black' : 'white'
}

// Get part icon
export function getPartIcon(
    part?: UIMessage['parts'][number] | null,
): string | undefined {
    if (isToolPart(part)) {
        if (part.type.includes('tool-subagent')) {
            return 'ri:robot-2-line'
        }
        if (part.type.includes('tool-mcp')) {
            return 'octicon:mcp-16'
        }
        if (part.type === 'tool-retrieveRevisedAnswers') {
            return 'ri:file-edit-line'
        }
        if (part.type === 'tool-searchInKnowledgeBase') {
            return 'ri:search-line'
        }
        if (part.type === 'tool-showMultipleChoice') {
            return 'ri:checkbox-multiple-line'
        }
        if (part.type === 'tool-showArtifact') {
            return 'ri:file-code-line'
        }
        if (part.type === 'tool-showEmail') {
            return 'ri:mail-line'
        }
        if (part.type === 'tool-showWebPages') {
            return 'ri:pages-line'
        }
        if (part.type === 'tool-showProducts') {
            return 'ri:shopping-bag-line'
        }
        if (part.type === 'tool-showContactForm') {
            return 'ri:send-plane-2-line'
        }
        return 'ri:tools-line'
    }
    if (isReasoningPart(part)) {
        return 'ri:lightbulb-line'
    }
    if (isTextPart(part)) {
        return 'ri:double-quotes-r'
    }
    return undefined
}

// Get part label
export function getToolPartLabel(
    $t: (key: string) => string,
    part?: UIMessage['parts'][number] | null,
): string | undefined {
    if (isToolPart(part)) {
        if (part.type === 'tool-retrieveRevisedAnswers') {
            return $t(`messagePart.revisedAnswers`)
        }
        if (part.type === 'tool-searchInKnowledgeBase') {
            return $t(`messagePart.searchingDocuments`)
        }
        if (part.type === 'tool-showMultipleChoice') {
            return $t(`messagePart.showMultipleChoice`)
        }
        if (part.type === 'tool-showArtifact') {
            return $t(`messagePart.showArtifact`)
        }
        if (part.type === 'tool-showEmail') {
            return $t(`messagePart.showEmail`)
        }
        if (part.type === 'tool-showWebPages') {
            return $t(`messagePart.showWebPages`)
        }
        if (part.type === 'tool-showProducts') {
            return $t(`messagePart.showProducts`)
        }
        if (part.type === 'tool-showContactForm') {
            return $t(`messagePart.showContactForm`)
        }
        return $t(`messagePart.tool`)
    }
    if (isReasoningPart(part)) {
        return $t(`messagePart.reasoning`)
    }
    return $t(`messagePart.streaming`)
}
